/**
 * ADA Rivyo Product Reviews keyboard shim — sandbox-only
 * Progressive enhancement for Rivyo (Thimatic) widgets rendered after load.
 * Does NOT modify Rivyo remote assets. Idempotent via data-ada-rivyo-bound.
 * Date: 2026-09-23
 * 2026-09-25 (FH-A11Y-2026-09-25 RIVYO LI SEMANTICS): star-filter rows and vote items keep their <li> a plain
 * list item. role=button / tabindex=0 / aria-label / aria-pressed and the Enter/Space handler now sit on an
 * existing inner element (filter: .wc_graph_star_contain; vote: .wc_rivyo_thumb_btn). No DOM is moved or
 * wrapped (Rivyo is React-rendered). Activation still calls the <li>'s own click, so mouse/touch are unchanged.
 * Fixes axe list / listitem / aria-allowed-role on ul.wc_review_summary_graph and ul.wc_review_like.
 */
(function () {
  var ATTR = "data-ada-rivyo-bound";
  var ROOT_MARK = "data-ada-rivyo-root";
  var observer = null;
  var scheduled = null;

  function isVisible(el) {
    if (!el || !el.getBoundingClientRect) return false;
    var cs = window.getComputedStyle(el);
    if (cs.display === "none" || cs.visibility === "hidden") return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  function activateLikeClick(el) {
    // Rivyo binds onClick on the parent <li>; prefer that, else the thumb span.
    if (!el) return;
    var li = el.closest ? el.closest("ul.wc_review_like > li") : null;
    (li || el).click();
  }

  function onActivateKey(e, activate) {
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    e.stopPropagation();
    activate();
  }

  function ensureName(el, name) {
    if (!el) return;
    if (!el.getAttribute("aria-label") && !el.getAttribute("aria-labelledby")) {
      el.setAttribute("aria-label", name);
    }
  }

  // FH-A11Y-2026-09-25 RIVYO LI SEMANTICS: the <li> stays a plain list item; the control lives on an inner element.
  function innerControl(li, selector) {
    var inner = li.querySelector(selector);
    if (!inner) {
      // fall back to the first element child (never the <li> itself)
      inner = li.firstElementChild;
    }
    return inner;
  }

  function releaseLi(li, label) {
    // Undo only what an earlier shim version put on the <li> (role/tabindex/name/pressed); never touch Rivyo's own attributes.
    var wasOurs = /^(filter|vote)$/.test(li.getAttribute(ATTR) || "");
    if (li.getAttribute("role") === "button") li.removeAttribute("role");
    if (li.getAttribute("tabindex") === "0") li.removeAttribute("tabindex");
    if (li.getAttribute("aria-label") === label) li.removeAttribute("aria-label");
    if (wasOurs) li.removeAttribute("aria-pressed");
  }

  function bindBadge(el) {
    if (!el || el.getAttribute(ATTR) === "badge") return;
    el.setAttribute(ATTR, "badge");
    if (el.tabIndex < 0) el.tabIndex = 0;
    if (!el.getAttribute("role")) el.setAttribute("role", "button");
    var avgEl = el.querySelector(".rivyoReviewAverage");
    var labelText = ((avgEl && avgEl.textContent) || el.textContent || "")
      .replace(/\s+/g, " ")
      .trim();
    ensureName(el, labelText ? ("Product reviews: " + labelText) : "Jump to product reviews");
    el.addEventListener("keydown", function (e) {
      onActivateKey(e, function () {
        el.click();
      });
    });
  }

  function filterLabel(el) {
    var rating = el.getAttribute("data-rating");
    var count = el.getAttribute("data-count");
    if (el.classList.contains("wc_all_rating_filter") || rating === "null" || rating === "") {
      var txt = (el.querySelector(".wc_filter_review_text") || el).textContent || "";
      return (txt.replace(/\s+/g, " ").trim() || "Show all reviews");
    }
    var parts = [];
    if (rating) parts.push(rating + " star" + (rating === "1" ? "" : "s"));
    if (count != null && count !== "") parts.push("(" + count + ")");
    return parts.length ? "Filter reviews: " + parts.join(" ") : "Filter reviews by star rating";
  }

  var FILTER_INNER = ".wc_graph_star_contain, .wc_filter_review_text";
  var FILTER_ROWS = ".wc_rating_filter, .wc_star_graph_line, .wc_all_rating_filter";

  function syncFilterPressed(el, ctl) {
    // Rivyo does not always mark active; infer from URL/param or aria we set on click.
    var active = el.classList.contains("wc_active") || ctl.getAttribute("aria-pressed") === "true";
    ctl.setAttribute("aria-pressed", active ? "true" : "false");
  }

  function setFilterPressed(el) {
    // Clear pressed on sibling rows' controls, set on this row's control (approximate active state)
    var list = el.parentElement;
    if (list) {
      list.querySelectorAll(FILTER_ROWS).forEach(function (sib) {
        if (sib === el) return;
        var c = sib.querySelector('[' + ATTR + '="filter"]');
        if (c) c.setAttribute("aria-pressed", "false");
      });
    }
    var own = el.querySelector('[' + ATTR + '="filter"]');
    if (own) own.setAttribute("aria-pressed", "true");
  }

  function bindStarFilter(el) {
    if (!el) return;
    // Skip zero-count rows Rivyo disables
    if (el.getAttribute("data-count") === "0") return;
    var ctl = innerControl(el, FILTER_INNER);
    if (!ctl || ctl.getAttribute(ATTR) === "filter") return;
    var label = filterLabel(el);
    releaseLi(el, label);
    el.setAttribute(ATTR, "filter-row");
    ctl.setAttribute(ATTR, "filter");
    if (ctl.tabIndex < 0) ctl.tabIndex = 0;
    if (!ctl.getAttribute("role")) ctl.setAttribute("role", "button");
    ensureName(ctl, label);
    syncFilterPressed(el, ctl);
    ctl.addEventListener("keydown", function (e) {
      onActivateKey(e, function () {
        setFilterPressed(el);
        el.click();
      });
    });
    if (!el.__adaRivyoFilterClick) {
      el.__adaRivyoFilterClick = true;
      el.addEventListener("click", function () {
        setFilterPressed(el);
      });
    }
  }

  function bindSortSelect(el) {
    if (!el || el.getAttribute(ATTR) === "sort") return;
    el.setAttribute(ATTR, "sort");
    if (el.tabIndex < 0) el.tabIndex = 0;
    el.removeAttribute("aria-hidden");
    if (el.hasAttribute("disabled")) {
      // leave disabled alone
    }
    ensureName(el, "Sort reviews");
    // Native <select> already supports keyboard; we only restore focusability + name + ring via CSS.
  }

  function bindRatingSelect(el) {
    if (!el || el.getAttribute(ATTR) === "rating-select") return;
    el.setAttribute(ATTR, "rating-select");
    if (el.tabIndex < 0) el.tabIndex = 0;
    el.removeAttribute("aria-hidden");
    ensureName(el, "Filter reviews by star rating");
  }

  function voteLabel(li) {
    var btn = li.querySelector(".wc_rivyo_thumb_btn") || li;
    var like = btn.getAttribute("data-like");
    var name = btn.getAttribute("data-name") || "Review";
    // Rivyo uses numeric like/dislike values; map by position / svg / text
    var text = (li.textContent || "").toLowerCase();
    var isDown =
      like === "0" ||
      like === "dislike" ||
      like === "2" ||
      /dislike|down/.test(text) ||
      (li.parentElement && li === li.parentElement.lastElementChild && like !== "1" && like !== "like");
    // Prefer data-like: commonly 1=up, 0=down (or "like"/"dislike")
    if (like === "1" || like === "like" || like === "up") isDown = false;
    if (like === "0" || like === "dislike" || like === "down") isDown = true;
    // If still ambiguous, use index in ul
    if (like == null || like === "") {
      var kids = li.parentElement ? Array.prototype.indexOf.call(li.parentElement.children, li) : 0;
      isDown = kids > 0;
    }
    return (isDown ? "Vote down" : "Vote up") + " this " + String(name).toLowerCase();
  }

  function bindVoteItem(li) {
    if (!li) return;
    var ctl = innerControl(li, ".wc_rivyo_thumb_btn");
    if (!ctl || ctl.getAttribute(ATTR) === "vote") return;
    var label = voteLabel(li);
    releaseLi(li, label);
    li.setAttribute(ATTR, "vote-item");
    ctl.setAttribute(ATTR, "vote");
    if (ctl.tabIndex < 0) ctl.tabIndex = 0;
    if (!ctl.getAttribute("role")) ctl.setAttribute("role", "button");
    ensureName(ctl, label);
    ctl.addEventListener("keydown", function (e) {
      onActivateKey(e, function () {
        activateLikeClick(li);
      });
    });
  }

  function enhance(root) {
    root = root || document;
    // 1) Star badge / jump control
    root.querySelectorAll(".rivyoReviewWidget.rivyoWidgetTrigger, .rivyoWidgetTrigger").forEach(bindBadge);

    // 2) Star-count filter graph rows
    root
      .querySelectorAll(
        "li.wc_rating_filter, li.wc_star_graph_line.wc_rating_filter, li.wc_all_rating_filter, .wc_rating_filter[data-rating]"
      )
      .forEach(bindStarFilter);

    // 3) Sort by native select
    root.querySelectorAll("select#sort_by, select.wc_review_type_drop, select[name='sort_by']").forEach(bindSortSelect);

    // 3b) Optional rating dropdown filter (also present in Rivyo UI)
    root.querySelectorAll("select.rivyoReviewRatingFilter, select#rating[name='rating']").forEach(bindRatingSelect);

    // 4) Vote up / down — click handler is on <li>
    root.querySelectorAll("ul.wc_review_like > li").forEach(bindVoteItem);
  }

  function scheduleEnhance() {
    if (scheduled) return;
    scheduled = window.requestAnimationFrame(function () {
      scheduled = null;
      enhance(document);
    });
  }

  function startObserver() {
    if (observer) return;
    var target = document.body;
    if (!target) return;
    target.setAttribute(ROOT_MARK, "1");
    observer = new MutationObserver(function () {
      scheduleEnhance();
    });
    observer.observe(target, { childList: true, subtree: true });
  }

  function init() {
    enhance(document);
    startObserver();
    // Rivyo hydrates late; a few deferred passes are cheap and idempotent
    [500, 1500, 3000, 6000, 10000].forEach(function (ms) {
      setTimeout(function () {
        enhance(document);
      }, ms);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  document.addEventListener("shopify:section:load", function () {
    enhance(document);
  });
})();

/* FH-A11Y-2026-09-25 RIVYO HEADINGS STARS START */
/*
 * DRAFT (not live). Append to assets/ada-rivyo-keyboard.js AFTER the existing shim IIFE. Remove START..END to roll back.
 * (1) Heading remap inside the Rivyo PDP widget (#wc_review_section): Rivyo renders h6 author, h5 review title, h6 "Reply",
 *     h4 question, h6 question author right after its own h2 "Reviews" -> skipped levels. We only add role="heading" +
 *     aria-level on the SAME elements (no tag swap, no DOM move, no class/style change -> 0 px, React-safe).
 *     Levels are relative to the widget's own title heading (base B, h2 on Cancun): author B+1, review title B+1,
 *     reply B+2, question B+1, question author B+2; any other deeper heading -> B+1 (B+2 inside a reply container).
 *     A heading is never pushed to a deeper level than its tag, and headings we did not mark are never overwritten.
 * (2) Star summaries on the PDP only (never collection grids / product cards): the Rivyo badge in the main product info
 *     (.rivyoReviewWidget) and the widget summary (.wc_review_summary_count). Appends a visually hidden
 *     "Rated X out of 5 stars, N reviews" read from the rendered Rivyo DOM (badge: .rivyoReviewAverage text, fallback
 *     data-rating; widget: .wc_average_review_stars[data-avg] + .wc_review_summary_count_text). The decorative star icons and
 *     the visible duplicate text get aria-hidden="true" (no visual effect). The badge is role=button from the shim with an
 *     aria-label; we only rewrite that label when it is still the shim's default ("Product reviews: ...").
 * Coexists with the 15:52 shim patch: never touches role/tabindex/keydown on .wc_graph_star_contain / .wc_rivyo_thumb_btn /
 * <li>s, never adds listeners to Rivyo controls. Idempotent (data-fh-rhs marks); rAF-debounced MutationObserver + the same
 * deferred passes as the shim, so it survives Rivyo React re-renders.
 */
(function () {
  if (window.__fhA11yRivyoHS) return;
  window.__fhA11yRivyoHS = true;
  var MARK = "data-fh-rhs";
  var WIDGET = "#wc_review_section";
  var SR_STYLE = "position:absolute!important;width:1px!important;height:1px!important;padding:0!important;margin:-1px!important;" +
    "overflow:hidden!important;clip:rect(0 0 0 0)!important;clip-path:inset(50%)!important;white-space:nowrap!important;border:0!important;";
  // product cards / grids (collection grids, related products, Globo cards). NOTE: Dawn's PDP info column is itself a div.grid__item,
  // so only list-item grid cells (li.grid__item) are excluded, not every .grid__item.
  var GRID = ".card-wrapper, .card, .product-card-wrapper, .spf-product-card, li.grid__item, .product-grid, product-recommendations, .product-recommendations, .related-products, [id*='related' i], [class*='related' i]";
  var scheduled = null;

  function norm(s) { return String(s || "").replace(/\s+/g, " ").trim(); }
  function tagLevel(h) { var m = /^H([1-6])$/.exec(h.tagName); return m ? +m[1] : null; }
  function effLevel(h) { var a = parseInt(h.getAttribute("aria-level"), 10); return a > 0 ? a : (tagLevel(h) || 2); }
  function setAttr(el, n, v) { if (el.getAttribute(n) !== v) el.setAttribute(n, v); }

  // ---------- (1) headings ----------
  var RULES = [
    [".wc_review_item_author_info h1, .wc_review_item_author_info h2, .wc_review_item_author_info h3, .wc_review_item_author_info h4, .wc_review_item_author_info h5, .wc_review_item_author_info h6", 1],
    ["h1.wc_review_item_body_title, h2.wc_review_item_body_title, h3.wc_review_item_body_title, h4.wc_review_item_body_title, h5.wc_review_item_body_title, h6.wc_review_item_body_title", 1],
    [".wc_review_item_reply h1, .wc_review_item_reply h2, .wc_review_item_reply h3, .wc_review_item_reply h4, .wc_review_item_reply h5, .wc_review_item_reply h6", 2],
    [".wc_question_author h1, .wc_question_author h2, .wc_question_author h3, .wc_question_author h4, .wc_question_author h5, .wc_question_author h6", 2],
    [".wc_question_widget_item h1, .wc_question_widget_item h2, .wc_question_widget_item h3, .wc_question_widget_item h4, .wc_question_widget_item h5, .wc_question_widget_item h6", 1]
  ];
  function remapHeadings(w) {
    var title = w.querySelector(".wc_review_head .wc_review_title h1, .wc_review_head .wc_review_title h2, .wc_review_head .wc_review_title h3, .wc_review_title h1, .wc_review_title h2, .wc_review_title h3");
    var base = title ? effLevel(title) : 2;
    var done = [];
    function apply(h, off) {
      if (done.indexOf(h) >= 0 || h === title) return; done.push(h);
      var tl = tagLevel(h); if (!tl) return;
      if (!h.hasAttribute(MARK) && (h.hasAttribute("aria-level") || (h.getAttribute("role") && h.getAttribute("role") !== "heading"))) return; // not ours
      var target = Math.min(6, base + off);
      if (tl <= target && !h.hasAttribute(MARK)) return; // already in order, leave native
      if (tl <= target) target = tl;
      if (h.getAttribute(MARK) !== "h" + tl) h.setAttribute(MARK, "h" + tl);
      setAttr(h, "role", "heading");
      setAttr(h, "aria-level", String(target));
    }
    RULES.forEach(function (r) { w.querySelectorAll(r[0]).forEach(function (h) { if (h.closest(".wc_question_author") && r[1] === 1 && r[0].indexOf("wc_question_widget_item") >= 0) return; apply(h, r[1]); }); });
    w.querySelectorAll("h1,h2,h3,h4,h5,h6").forEach(function (h) { apply(h, h.closest("[class*='reply' i]") ? 2 : 1); });
  }

  // ---------- (2) star summaries ----------
  function srSpan(host, text) {
    var s = null;
    for (var i = 0; i < host.children.length; i++) if (host.children[i].getAttribute(MARK) === "sr") { s = host.children[i]; break; }
    if (!s) {
      s = document.createElement("span"); s.setAttribute(MARK, "sr"); s.setAttribute("style", SR_STYLE); s.textContent = text;
      host.appendChild(s); return;
    }
    if (s.textContent !== text) s.textContent = text;
  }
  function hide(el) { if (el && el.getAttribute("aria-hidden") !== "true") { el.setAttribute("aria-hidden", "true"); el.setAttribute(MARK + "-hid", "1"); } }
  function phrase(x, n) {
    var t = "Rated " + x + " out of 5 stars";
    if (n != null) t += ", " + n + (n === 1 ? " review" : " reviews");
    return t;
  }
  function num(s) { var m = /(\d+(?:[.,]\d+)?)/.exec(s || ""); return m ? m[1].replace(",", ".") : null; }

  function badge(host) {
    if (host.closest(GRID) || host.closest(WIDGET)) return;
    if (!host.closest(".product__info-container, product-info, [id^='ProductInfo-'], .product__info-wrapper, [id^='MainProduct-']")) return;
    var avgEl = host.querySelector(".rivyoReviewAverage");
    var avgTxt = norm(avgEl && avgEl.textContent);
    var x = num(avgTxt) || num(host.getAttribute("data-rating"));
    if (!x || +x <= 0) return; // nothing rendered yet (or no reviews): leave as is
    var mN = /(\d[\d,]*)\s*review/i.exec(avgTxt);
    var n = mN ? parseInt(mN[1].replace(/,/g, ""), 10) : null;
    var text = phrase(x, n);
    host.querySelectorAll(".rivyoReviewIcon").forEach(hide);
    hide(avgEl);
    srSpan(host, text);
    var lab = host.getAttribute("aria-label");
    if (lab && /^Product reviews: /.test(lab)) setAttr(host, "aria-label", "Product reviews: " + text);
  }

  function widgetSummary(host) {
    var stars = host.querySelector(".wc_average_review_stars");
    var cntEl = host.querySelector(".wc_review_summary_count_text");
    var x = stars && num(stars.getAttribute("data-avg"));
    if (!x && stars) { var f = stars.querySelector(".wc_review_badge_star_fill"); var pct = f && parseFloat(f.style.width); if (pct > 0) x = String(Math.round(pct / 20 * 10) / 10); }
    if (!x || +x <= 0) return;
    var nTxt = norm(cntEl && cntEl.textContent); var n = /\d/.test(nTxt) ? parseInt(nTxt.replace(/[^\d]/g, ""), 10) : null;
    hide(stars);
    if (n != null) hide(cntEl);
    srSpan(host, phrase(x, n));
  }

  function run() {
    if (!/\/products\//.test(location.pathname)) return;
    var w = document.querySelector(WIDGET);
    if (w) { remapHeadings(w); w.querySelectorAll(".wc_review_summary_count").forEach(widgetSummary); }
    document.querySelectorAll(".rivyoReviewWidget").forEach(badge);
  }
  function schedule() {
    if (scheduled) return;
    scheduled = (window.requestAnimationFrame || setTimeout)(function () { scheduled = null; try { run(); } catch (e) {} });
  }
  function init() {
    run();
    if (document.body && window.MutationObserver) {
      new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true, attributes: true,
        attributeFilter: ["data-avg", "data-rating", "data-ada-rivyo-bound"] });
    }
    [500, 1500, 3000, 6000, 10000].forEach(function (ms) { setTimeout(schedule, ms); });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init); else init();
  document.addEventListener("shopify:section:load", schedule);
})();
/* FH-A11Y-2026-09-25 RIVYO HEADINGS STARS END */
